// src/hooks/use-biometric-setup.ts
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { profileApi } from '../api/profile/profile';
import { API_BASE_URL, ENVIRONMENT } from '../env';
import { detectMobilePlatform, getPlatformName } from '../helpers/biometric';
import { isNative } from '../platform';
import {
  isNativeBiometricAvailable,
  promptNativeBiometric,
  storeNativeCredentials,
} from '../helpers/native-biometric';
import { BiometricsStorage } from '../helpers/biometrics-storage';
import { decodeJwtPayload } from '../api/token-storage';

export type BiometricErrorType = 'user-cancelled' | 'unavailable' | 'network-or-server' | null;

type SetupState = {
  status: string | null;
  error: string | null;
  errorType: BiometricErrorType;
  isLoading: boolean;
  platformAvailable: boolean | null;
  secureContext: boolean;
  conditionalMediationAvailable: boolean | null;
};

const isDev = ENVIRONMENT === 'DEV';
const log = (...args: any[]) => { if (isDev) console.log(...args); };
const logWarn = (...args: any[]) => { if (isDev) console.warn(...args); };
const logInfo = (...args: any[]) => { if (isDev) console.info(...args); };
const logGroup = (...args: any[]) => { if (isDev) console.groupCollapsed(...args); };
const logGroupEnd = () => { if (isDev) console.groupEnd(); };

function makeReqId() {
  return `webauthn_reg_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

async function gatherClientDiagnostics() {
  const { isIos, isAndroid, isMobile } = detectMobilePlatform();
  
  const uvpa =
    typeof PublicKeyCredential !== 'undefined' &&
    typeof (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable === 'function'
      ? await (PublicKeyCredential as any)
          .isUserVerifyingPlatformAuthenticatorAvailable()
          .catch(() => null)
      : null;

  const cma =
    typeof PublicKeyCredential !== 'undefined' &&
    typeof (PublicKeyCredential as any).isConditionalMediationAvailable === 'function'
      ? await (PublicKeyCredential as any).isConditionalMediationAvailable().catch(() => null)
      : null;

  const pp: any = (document as any).permissionsPolicy || (document as any).featurePolicy;
  let allowsCreate: boolean | null = null;
  let allowsGet: boolean | null = null;
  try {
    if (pp?.allowsFeature) {
      allowsCreate = pp.allowsFeature('publickey-credentials-create');
      allowsGet = pp.allowsFeature('publickey-credentials-get');
    }
  } catch {
    // Ignore any errors from permissions policy checks (e.g. older browsers)
  }

  return {
    ts: new Date().toISOString(),
    href: location.href,
    origin: location.origin,
    hostname: location.hostname,
    ua: navigator.userAgent,
    platform: (navigator as any).platform,
    language: navigator.language,
    isSecureContext: window.isSecureContext === true,
    isFramed: window.top !== window.self,
    uvpa,
    conditionalMediationAvailable: cma,
    permissionsPolicy: { allowsCreate, allowsGet },
    isIos,
    isAndroid,
    isMobile,
  };
}

function secureContextHelp(): string {
  const host = location.hostname;
  const proto = location.protocol;
  if (proto === 'https:') return 'Secure context OK.';
  if (host === 'localhost' || host === '127.0.0.1')
    return 'Secure context OK (localhost exception).';

  return (
    'WebAuthn requires HTTPS (secure context). ' +
    `Current origin is ${location.origin}. ` +
    'If you are testing on Android, do not use http://<LAN-IP>: use https (dev tunnel) or localhost via adb reverse.'
  );
}

export function useBiometricSetup() {
  const [status, setStatus] = useState<SetupState['status']>(null);
  const [error, setError] = useState<SetupState['error']>(null);
  const [errorType, setErrorType] = useState<SetupState['errorType']>(null);
  const [isLoading, setIsLoading] = useState<SetupState['isLoading']>(false);
  const [platformAvailable, setPlatformAvailable] = useState<SetupState['platformAvailable']>(null);
  const [secureContext, setSecureContext] = useState<SetupState['secureContext']>(false);
  const [conditionalMediationAvailable, setConditionalMediationAvailable] =
    useState<SetupState['conditionalMediationAvailable']>(null);

  useEffect(() => {
    setSecureContext(window.isSecureContext === true);

    // Platform authenticator probe (Windows Hello / TouchID / FaceID etc.)
    if (
      'PublicKeyCredential' in window &&
      typeof (PublicKeyCredential as any).isUserVerifyingPlatformAuthenticatorAvailable ===
        'function'
    ) {
      (PublicKeyCredential as any)
        .isUserVerifyingPlatformAuthenticatorAvailable()
        .then((v: boolean) => {
          logInfo('[WebAuthn][register] UVPA available:', v);
          setPlatformAvailable(v);
        })
        .catch(() => {
          logWarn('[WebAuthn][register] UVPA probe failed');
          setPlatformAvailable(null);
        });
    } else {
      logWarn('[WebAuthn][register] PublicKeyCredential or UVPA API not present');
      setPlatformAvailable(null);
    }

    // Conditional mediation (useful for passkey login UX, browser autofill)
    if (
      'PublicKeyCredential' in window &&
      typeof (PublicKeyCredential as any).isConditionalMediationAvailable === 'function'
    ) {
      (PublicKeyCredential as any)
        .isConditionalMediationAvailable()
        .then((v: boolean) => {
          logInfo('[WebAuthn][cap] conditional mediation available:', v);
          setConditionalMediationAvailable(v);
        })
        .catch(() => {
          setConditionalMediationAvailable(null);
        });
    } else {
      setConditionalMediationAvailable(null);
    }
  }, []);

  const handleSetup = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setErrorType(null);
    setStatus(null);

    const reqId = makeReqId();
    const startedAt = performance.now();

    try {
      logGroup('[WebAuthn][register] START', reqId);

      let profile: { id: string; email_address?: string | null };
      try {
        profile = await profileApi.getMe();
        log('profileApi.getMe():', profile);
      } catch (meErr) {
        // Fallback: during onboarding the token may not yet be in Axios headers
        // (race between setSession and React re-render). Extract from localStorage/JWT.
        const accessToken = localStorage.getItem('access_token') ?? '';
        const jwtPayload = accessToken ? decodeJwtPayload(accessToken) : null;
        const fallbackId = localStorage.getItem('user_id') ?? jwtPayload?.sub ?? '';
        const fallbackEmail = jwtPayload?.email ?? BiometricsStorage.getEmail() ?? '';
        log('[WebAuthn][register] profileApi.getMe() failed, falling back to JWT/localStorage', { fallbackId, fallbackEmail, err: (meErr as any)?.message });
        profile = { id: fallbackId, email_address: fallbackEmail };
      }

      if (!profile.id || !profile.email_address) throw new Error('User not authenticated');

      // ─── NATIVE PATH: use Capacitor biometric plugin ───
      if (isNative()) {
        const nativeBio = await isNativeBiometricAvailable();
        if (!nativeBio.available) {
          const err = new Error('Biometric authentication is not available on this device.');
          (err as any).errorCategory = 'unavailable' as BiometricErrorType;
          throw err;
        }

        log('[WebAuthn][register] native biometryType:', nativeBio.biometryType, 'raw:', nativeBio.rawBiometryType);
        setStatus('Verifying your identity…');

        try {
          await promptNativeBiometric('Enroll biometrics for Mailander');
        } catch (biometricErr: any) {
          log('[WebAuthn][register] native biometric error:', biometricErr?.message, biometricErr);
          const msg = biometricErr?.message?.includes('timed out')
            ? 'Biometric prompt timed out. Please try again.'
            : 'Biometric enrollment was cancelled.';
          const err = new Error(msg);
          (err as any).errorCategory = 'user-cancelled' as BiometricErrorType;
          throw err;
        }

        // Store the user's refresh token in the secure enclave
        const refreshToken = localStorage.getItem('refresh_token');
        console.log('[NativeBio] profile.id:', profile.id, 'refreshToken exists:', !!refreshToken);
        if (refreshToken) {
          try {
            await storeNativeCredentials(profile.id, refreshToken);
            console.log('[NativeBio] storeNativeCredentials succeeded');
          } catch (storeErr) {
            console.error('[NativeBio] storeNativeCredentials FAILED:', storeErr);
          }
          // Verify read-back
          try {
            const { getNativeCredentials } = await import('../helpers/native-biometric');
            const readBack = await getNativeCredentials();
            console.log('[NativeBio] read-back:', readBack ? 'found' : 'NOT FOUND', readBack);
          } catch (readErr) {
            console.error('[NativeBio] read-back FAILED:', readErr);
          }
        } else {
          console.warn('[NativeBio] NO refresh_token in localStorage — skipping credential store');
        }

        // Update profile on backend
        try {
          await profileApi.updateMe({ biometrics_enabled: true });
        } catch {
          log('[WebAuthn][register] native profile update fallback failed (non-critical)');
        }

        // Persist biometrics preference so login screen auto-uses biometric login
        BiometricsStorage.setBiometricsEnabled(true);
        BiometricsStorage.setBiometricsType(nativeBio.biometryType);

        setStatus('Biometric enrollment successful ✓');
        return true;
      }

      // ─── WEB PATH: existing WebAuthn flow ───
      if (!window.isSecureContext) {
        throw new Error(secureContextHelp());
      }
      if (!('PublicKeyCredential' in window)) {
        throw new Error('WebAuthn is not supported in this browser.');
      }

      const diag = await gatherClientDiagnostics();
      log('[WebAuthn][register] diagnostics:', diag);

      // Do NOT hard-block based on UVPA/platformAvailable:
      // Mobile platforms (iOS/Android) sometimes report false but still work
      if (platformAvailable === false || (diag as any).uvpa === false) {
        const platformName = getPlatformName((diag as any).isIos, (diag as any).isAndroid);
        logWarn(
          `[WebAuthn][register] Platform authenticator probe indicates unavailable on ${platformName}, proceeding anyway (mobile devices may still support biometrics).`,
          { platformAvailable, uvpa: (diag as any).uvpa, isMobile: (diag as any).isMobile }
        );
      }

      setStatus(
        `Requesting registration options… (origin=${location.origin}, host=${location.hostname})`
      );

      const accessToken = localStorage.getItem('access_token');
      log('[WebAuthn][register] access_token present:', !!accessToken);
      if (!accessToken) throw new Error('No access token available');

      const optionsURL = `${API_BASE_URL}/webauthn/generate-registration-options?debug=1`;
      log('[WebAuthn][register] POST optionsURL:', optionsURL);

      const optionsPayload = { userID: profile.id, username: profile.email_address, diag };
      log('[WebAuthn][register] options payload:', optionsPayload);

      const { data: optionsRes } = await axios.post(optionsURL, optionsPayload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-WebAuthn-Debug': '1',
          'X-Request-Id': reqId,
        },
        withCredentials: false,
      });

      log('[WebAuthn][register] options response:', optionsRes);

      const optionsJSON = (optionsRes as any)?.optionsJSON;
      const challenge_id = (optionsRes as any)?.challenge_id;

      if (!optionsJSON?.challenge) {
        log('[WebAuthn][register] malformed options response:', optionsRes);
        throw new Error('Server returned malformed registration options. Please try again.');
      }
      if (!challenge_id) {
        log('[WebAuthn][register] missing challenge_id in response:', optionsRes);
        throw new Error('Server returned incomplete registration options. Please try again.');
      }

      setStatus('Starting WebAuthn registration…');

      let attResp: any;
      try {
        log('[WebAuthn][register] startRegistration() call', { reqId });

        const timeout = 30_000;
        const registrationPromise = startRegistration({ optionsJSON });
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Biometric prompt timeout')), timeout)
        );

        // X8: Progress feedback at 15s mark
        const progressTimer = setTimeout(() => {
          setStatus('This is taking longer than expected…');
        }, 15_000);

        try {
          attResp = await Promise.race([registrationPromise, timeoutPromise]);
        } finally {
          clearTimeout(progressTimer);
        }
        log('[WebAuthn][register] startRegistration() OK', attResp);
      } catch (regErr: any) {
        const errName = regErr?.name;
        if (errName === 'NotAllowedError') {
          const err = new Error('Biometric enrollment was cancelled.');
          (err as any).errorCategory = 'user-cancelled' as BiometricErrorType;
          throw err;
        } else if (errName === 'AbortError' || regErr?.message?.includes('timeout')) {
          const err = new Error('Biometric enrollment timed out. Please try again.');
          (err as any).errorCategory = 'user-cancelled' as BiometricErrorType;
          throw err;
        } else {
          throw new Error('We couldn\'t enroll biometrics. Please try again.');
        }
      }

      setStatus('Verifying response with server…');

      const verifyURL = `${API_BASE_URL}/webauthn/verify-registration`;
      log('[WebAuthn][register] POST verifyURL:', verifyURL);

      const verifyPayload = {
        userID: profile.id,
        challenge_id,
        clientDiag: diag,
        credential: {
          id: attResp.id,
          rawId: attResp.rawId,
          type: attResp.type,
          response: {
            attestationObject: (attResp.response as any).attestationObject,
            clientDataJSON: (attResp.response as any).clientDataJSON,
          },
          clientExtensionResults: attResp.clientExtensionResults,
          transports: (attResp.response as any).getTransports?.() ?? undefined,
        },
      };

      log('[WebAuthn][register] verify payload:', verifyPayload);

      const { data: verify } = await axios.post(verifyURL, verifyPayload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          'X-Request-Id': reqId,
        },
        withCredentials: false,
      });

      log('[WebAuthn][register] verify response:', verify);

      if (!verify?.success) {
        throw new Error(verify?.error || 'Verification failed');
      }

      // D1: Frontend fallback — ensure profile biometrics_enabled is set
      try {
        await profileApi.updateMe({ biometrics_enabled: true });
      } catch {
        log('[WebAuthn][register] profile update fallback failed (non-critical)');
      }

      // Persist biometrics preference locally so login screen auto-uses biometric login.
      // (Native path already does this at line 209; web path was missing it.)
      BiometricsStorage.setBiometricsEnabled(true);
      BiometricsStorage.setBiometricsType('unknown'); // WebAuthn doesn't report biometry type
      // Store email so biometric login can pass it for targeted authentication
      // (avoids discoverable credential flow which shows Google Credential Manager on Android)
      BiometricsStorage.setEmail(profile.email_address);

      const ms = Math.round(performance.now() - startedAt);
      log(`[WebAuthn][register] SUCCESS in ${ms}ms`, { reqId });

      setStatus('Biometric enrollment successful ✓');
      return true;
    } catch (e: any) {
      setStatus(null);

      // Determine error type from the caught error
      let errorCategory: BiometricErrorType = 'network-or-server';
      if (e?.errorCategory) {
        errorCategory = e.errorCategory;
      } else if (e?.response?.status) {
        // HTTP error - treat as network/server error
        errorCategory = 'network-or-server';
      }

      setErrorType(errorCategory);

      const axiosStatus = e?.response?.status;
      const axiosData = e?.response?.data;
      const axiosText = (() => {
        try {
          return typeof axiosData === 'string' ? axiosData : JSON.stringify(axiosData);
        } catch {
          return String(axiosData);
        }
      })();

      const name = (e as DOMException)?.name;
      const msg = e?.message || e?.response?.data?.detail || e?.response?.data?.error || 'Unknown error';

      const composed = [
        name ? `${name}: ${msg}` : msg,
        axiosStatus ? `(HTTP ${axiosStatus})` : null,
        axiosData ? `backend=${axiosText}` : null,
      ]
        .filter(Boolean)
        .join(' ');

      setError(composed);

      logGroup('[WebAuthn][register] ERROR', reqId);
      if (isDev) console.error(e);
      log('axiosStatus:', axiosStatus);
      log('axiosData:', axiosData);
      log('errorCategory:', errorCategory);
      logGroupEnd();

      return false;
    } finally {
      logGroupEnd();
      setIsLoading(false);
    }
  }, [platformAvailable]);

  return {
    status,
    error,
    errorType,
    handleSetup,
    isLoading,
    platformAvailable,
    secureContext,
    conditionalMediationAvailable,
  };
}
